import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { slugify, formatArticleDate, listArticlesForCommerce, type BlogArticle } from '@/lib/blog';
import ImagePicker from '@/components/feature/ImagePicker';
import BlogRichEditor from '@/components/feature/BlogRichEditor';
import { runDocumentAi } from '@/lib/documentAi';
import { uploadMediaFile } from '@/hooks/useUpload';

// ─────────────────────────────────────────────────────────────
// Blog du marchand — écriture, couverture, publication.
//
// Points de vigilance corrigés :
// • `blog_articles.author_id` est NOT NULL → on enregistre toujours un
//   identifiant d'auteur numérique (jamais null).
// • `blog_articles.slug` est NOT NULL → un slug de secours est généré
//   si le titre ne produit rien d'exploitable.
// • Les boutons « Brouillon » et « Publier » écrivent réellement le statut
//   et on relit l'article depuis la base après enregistrement.
// ─────────────────────────────────────────────────────────────

const EMPTY: Partial<BlogArticle> = {
  title: '',
  excerpt: '',
  content: '',
  cover_image: '',
  tags: '',
  status: 'draft',
};

const FALLBACK_COVERS = [
  'https://readdy.ai/api/search-image?query=Editorial%20magazine%20photograph%20of%20a%20bright%20warm%20workspace%20with%20natural%20light%2C%20soft%20neutral%20tones%2C%20minimal%20composition%2C%20professional%20blog%20illustration&width=1200&height=800&seq=blog-cover-default&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Hands%20writing%20in%20a%20notebook%20on%20a%20warm%20wooden%20table%20with%20coffee%20cup%2C%20cream%20and%20terracotta%20tones%2C%20soft%20morning%20light%2C%20editorial%20lifestyle%20photography&width=1200&height=800&seq=blog-cover-writing&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Small%20artisan%20shop%20interior%20with%20handmade%20products%20on%20wooden%20shelves%2C%20warm%20beige%20and%20olive%20tones%2C%20natural%20daylight%2C%20authentic%20craft%20atmosphere&width=1200&height=800&seq=blog-cover-shop&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Clean%20flat%20lay%20of%20desktop%20with%20documents%2C%20pen%20and%20laptop%20on%20cream%20surface%2C%20minimal%20warm%20tones%2C%20soft%20shadows%2C%20professional%20editorial%20photography&width=1200&height=800&seq=blog-cover-office&orientation=landscape',
];

/** Retire les puces / dièses en début de ligne (style IA repérable). */
function cleanContent(raw: string): string {
  return raw
    .split('\n')
    .map((line) => {
      const trimmed = line.replace(/^\s*[#*•▪◦\-–—>]+\s*/, '').trim();
      if (!trimmed) return '';
      const isHeading = trimmed.length <= 70 && !/[.!?]$/.test(trimmed);
      return isHeading ? `## ${trimmed}` : trimmed;
    })
    .filter(Boolean)
    .join('\n');
}

function pickCover(title: string): string {
  const seed = Array.from(title).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return FALLBACK_COVERS[seed % FALLBACK_COVERS.length];
}

/** Slug garanti non vide et unique côté client. */
function safeSlug(title: string, fallbackId: number | null): string {
  const base = slugify(title);
  if (base) return base.slice(0, 80);
  return `article-${fallbackId || Date.now()}`;
}

function buildInstruction(title: string, tags: string, excerpt: string): string {
  const theme = tags.trim() ? `Mots-clés à couvrir naturellement : ${tags.trim()}.` : '';
  const angle = excerpt.trim() ? `Angle souhaité : ${excerpt.trim()}.` : '';
  return [
    `Rédige un article de blog en français de 500 à 700 mots sur le sujet : « ${title} ».`,
    theme,
    angle,
    'Écris comme un rédacteur humain : ton chaleureux, phrases de longueur variée, exemples concrets.',
    'Structure : introduction de 2 phrases, 3 à 4 sous-titres courts, conclusion avec un appel à l’action simple.',
    'N’utilise AUCUN caractère spécial en début de ligne : pas de #, *, -, ni de numérotation.',
    'N’écris jamais « en tant qu’IA » et ne mets pas de titre principal (il existe déjà).',
  ].filter(Boolean).join('\n');
}

export default function MerchantBlogPage() {
  const { user } = useAuth();
  const commerceId = Number(user?.id || 0);
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [editing, setEditing] = useState<Partial<BlogArticle> | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    if (!commerceId) {
      setArticles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setArticles(await listArticlesForCommerce(commerceId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les articles.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      if (filter === 'published' && a.status !== 'published') return false;
      if (filter === 'draft' && a.status !== 'draft') return false;
      if (!q) return true;
      return `${a.title} ${a.excerpt || ''} ${a.tags || ''}`.toLowerCase().includes(q);
    });
  }, [articles, search, filter]);

  const toast = (text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(''), 4500);
  };

  const save = async (publish: boolean) => {
    if (!editing) return;
    const title = String(editing.title || '').trim();
    if (!title) {
      toast("Donnez un titre à l'article avant d'enregistrer.");
      return;
    }
    setSaving(true);
    try {
      const rawContent = String(editing.content || '');
      const isHtml = /<[a-z][\s\S]*>/i.test(rawContent);
      const body = isHtml ? rawContent : cleanContent(rawContent);
      const authorId = Number(editing.author_id || commerceId || user?.id || 0);

      const payload: Record<string, unknown> = {
        title,
        slug: safeSlug(editing.slug || title, editing.id ?? null),
        excerpt: String(editing.excerpt || '').trim() || null,
        content: body || ' ',
        cover_image: String(editing.cover_image || '').trim() || pickCover(title),
        tags: String(editing.tags || '').trim() || null,
        status: publish ? 'published' : 'draft',
        published_at: publish ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
        author_id: authorId > 0 ? authorId : null,
        idcommerce: commerceId || null,
      };

      if (!payload.author_id) {
        delete payload.author_id;
      }

      let savedId = editing.id ?? null;
      if (editing.id) {
        const { error: updError } = await supabase
          .from('blog_articles')
          .update(payload)
          .eq('id', editing.id);
        if (updError) throw updError;
      } else {
        const { data: inserted, error: insError } = await supabase
          .from('blog_articles')
          .insert(payload)
          .select('id')
          .maybeSingle();
        if (insError) throw insError;
        savedId = inserted?.id ?? null;
      }

      // Relecture pour confirmer que le statut a bien été écrit.
      if (savedId) {
        const { data: check } = await supabase
          .from('blog_articles')
          .select('id, status')
          .eq('id', savedId)
          .maybeSingle();
        const expected = publish ? 'published' : 'draft';
        if (check && check.status !== expected) {
          const { error: fixError } = await supabase
            .from('blog_articles')
            .update({ status: expected, published_at: publish ? new Date().toISOString() : null })
            .eq('id', savedId);
          if (fixError) throw fixError;
        }
      }

      setEditing(null);
      toast(publish ? 'Article publié sur votre site.' : 'Brouillon enregistré.');
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Enregistrement impossible.";
      toast(`Enregistrement impossible : ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const writeWithAi = async () => {
    if (!editing) return;
    const title = String(editing.title || '').trim();
    if (!title) {
      toast("Indiquez d'abord un titre : l'assistant s'en sert comme sujet.");
      return;
    }
    setAiBusy(true);
    try {
      const { result } = await runDocumentAi({
        action: 'write',
        instruction: buildInstruction(title, String(editing.tags || ''), String(editing.excerpt || '')),
      });
      const cleaned = cleanContent(result.replace(/\*\*/g, ''));
      if (!cleaned) throw new Error('Réponse vide');
      const plain = cleaned.replace(/^##\s*/gm, '');
      const autoExcerpt = plain.split('\n').filter(Boolean)[1] || plain;
      setEditing((prev) => (prev ? {
        ...prev,
        content: cleaned,
        excerpt: String(prev.excerpt || '').trim() || autoExcerpt.slice(0, 180),
        cover_image: String(prev.cover_image || '').trim() || pickCover(title),
      } : prev));
      toast('Article rédigé. Relisez et ajustez à votre voix avant de publier.');
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'assistant n'a pas pu écrire l'article.");
    } finally {
      setAiBusy(false);
    }
  };

  const togglePublish = async (a: BlogArticle) => {
    const publish = a.status !== 'published';
    try {
      const { error: e } = await supabase
        .from('blog_articles')
        .update({
          status: publish ? 'published' : 'draft',
          published_at: publish ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', a.id);
      if (e) throw e;
      await load();
      toast(publish ? 'Article publié.' : 'Article repassé en brouillon.');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Changement de statut impossible.');
    }
  };

  const remove = async (id: number) => {
    try {
      const { error: e } = await supabase.from('blog_articles').delete().eq('id', id);
      if (e) throw e;
      setDeleteId(null);
      await load();
    } catch {
      toast('Suppression impossible.');
    }
  };

  const uploadCover = async (file?: File) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadMediaFile(file, 'blog-cover');
      setEditing((prev) => (prev ? { ...prev, cover_image: url } : prev));
      toast('Image de couverture ajoutée.');
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setCoverUploading(false);
      if (coverInput.current) coverInput.current.value = '';
    }
  };

  const publishedCount = articles.filter((a) => a.status === 'published').length;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Blog</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Rédigez des articles, ajoutez des images où vous voulez et publiez-les sur votre site.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold whitespace-nowrap">
            {publishedCount} en ligne
          </span>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            <i className="ri-add-line"></i>Nouvel article
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5 mb-5">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un article…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>
        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit">
          {[
            { key: 'all' as const, label: 'Tous' },
            { key: 'published' as const, label: 'Publiés' },
            { key: 'draft' as const, label: 'Brouillons' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                filter === t.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => void load()} className="underline font-medium cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/60 rounded-xl px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-article-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-800">Aucun article</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-sm">
            Écrivez votre premier article : il apparaîtra dans la section Blog de votre site.
          </p>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600"
          >
            <i className="ri-add-line"></i>Écrire un article
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {filtered.map((a) => (
            <div key={a.id} className="group flex flex-col rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-300/80 transition-colors">
              <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
                {a.cover_image ? (
                  <img src={a.cover_image} alt={a.title} title={a.title} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-foreground-300"></i>
                  </div>
                )}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${a.status === 'published' ? 'bg-accent-500 text-background-50' : 'bg-background-50/95 text-foreground-700'}`}>
                  {a.status === 'published' ? 'Publié' : 'Brouillon'}
                </span>
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h2 className="text-sm font-semibold text-foreground-900 line-clamp-2">{a.title}</h2>
                <p className="text-[11px] text-foreground-500 mt-1 line-clamp-2 flex-1">{a.excerpt || 'Sans résumé'}</p>
                <p className="text-[10px] text-foreground-400 mt-2">
                  {formatArticleDate(a.published_at || a.created_at)}
                </p>
                <div className="mt-2.5 flex items-center gap-1.5">
                  <button
                    onClick={() => setEditing(a)}
                    className="flex-1 py-2 rounded-full bg-background-100 text-foreground-700 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
                  >
                    <i className="ri-edit-line mr-1"></i>Modifier
                  </button>
                  <button
                    onClick={() => togglePublish(a)}
                    className="py-2 px-2.5 rounded-full bg-accent-50 text-accent-700 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-100 transition-colors"
                    title={a.status === 'published' ? 'Dépublier' : 'Publier'}
                  >
                    <i className={a.status === 'published' ? 'ri-eye-off-line' : 'ri-send-plane-line'}></i>
                  </button>
                  <button
                    onClick={() => setDeleteId(a.id)}
                    className="py-2 px-2.5 rounded-full bg-background-100 text-red-500 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-red-50 transition-colors"
                    title="Supprimer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Éditeur plein écran */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-background-100/70 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl bg-background-50 flex flex-col md:my-4 md:rounded-2xl overflow-hidden border border-background-200/70">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-background-200/70 flex-shrink-0">
              <div className="min-w-0">
                <h2 className="text-base font-bold font-heading text-foreground-950 truncate">
                  {editing.id ? "Modifier l'article" : 'Nouvel article'}
                </h2>
                <p className="text-[11px] text-foreground-400">
                  Le contenu s'enregistre au format riche : les images restent là où vous les placez.
                </p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Titre</label>
                <input
                  value={String(editing.title || '')}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="Un titre clair et concret"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Résumé</label>
                <textarea
                  value={String(editing.excerpt || '')}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  rows={2}
                  maxLength={300}
                  placeholder="Deux phrases qui donnent envie de lire."
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-foreground-600">Contenu</label>
                  <span className="text-[10px] text-foreground-400 hidden sm:inline">
                    Placez le curseur, puis « Insérer une image »
                  </span>
                </div>
                <button
                  type="button"
                  onClick={writeWithAi}
                  disabled={aiBusy}
                  className="w-full mb-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 disabled:opacity-60 transition-colors"
                >
                  {aiBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
                  {aiBusy ? 'L’assistant rédige…' : "Écrire cet article avec l'assistant IA"}
                </button>
                <BlogRichEditor
                  value={String(editing.content || '')}
                  onChange={(html) => setEditing((prev) => (prev ? { ...prev, content: html } : prev))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Image de couverture</label>
                  <div className="flex items-start gap-2.5">
                    <div className="w-20 h-14 rounded-md overflow-hidden bg-background-100 border border-background-200/70 flex-shrink-0">
                      {editing.cover_image ? (
                        <img src={String(editing.cover_image)} alt="Couverture" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-foreground-300"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 min-w-0">
                      <input
                        ref={coverInput}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => void uploadCover(e.target.files?.[0])}
                      />
                      <button
                        type="button"
                        onClick={() => coverInput.current?.click()}
                        disabled={coverUploading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60"
                      >
                        <i className={coverUploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'}></i>
                        {coverUploading ? 'Envoi…' : "Téléverser une image"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                      >
                        <i className="ri-image-line"></i>Bibliothèque
                      </button>
                      {editing.cover_image && (
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, cover_image: '' })}
                          className="text-left px-1 text-[10px] text-foreground-400 hover:text-red-500 cursor-pointer whitespace-nowrap"
                        >
                          Retirer l'image
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-foreground-400 mt-1.5">
                    Sans image, une couverture cohérente est choisie automatiquement.
                  </p>
                </div>

                <div className="min-w-0">
                  <label className="block text-xs font-semibold text-foreground-600 mb-1">Mots-clés</label>
                  <input
                    value={String(editing.tags || '')}
                    onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                    placeholder="artisanat, conseils, nouveautés"
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                  <p className="text-[10px] text-foreground-400 mt-1.5">Séparés par des virgules.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 px-4 md:px-6 py-3.5 border-t border-background-200/70 flex-shrink-0 bg-background-50">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-600 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
              >
                Annuler
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => save(false)}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-secondary-600 disabled:opacity-50"
                >
                  Brouillon
                </button>
                <button
                  onClick={() => save(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50"
                >
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                  Publier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteId != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteId(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl border border-background-200/70 p-5">
            <h3 className="text-base font-bold font-heading text-foreground-950 mb-1">Supprimer cet article ?</h3>
            <p className="text-sm text-foreground-500 mb-4">Cette action est définitive.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm font-medium cursor-pointer hover:bg-background-200/70">
                Annuler
              </button>
              <button onClick={() => remove(deleteId)} className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-semibold cursor-pointer hover:bg-red-600">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      <ImagePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => setEditing((prev) => (prev ? { ...prev, cover_image: url } : prev))}
        selectedUrl={String(editing?.cover_image || '')}
      />

      {notice && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] w-[calc(100vw-2rem)] max-w-sm">
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-xs font-medium">
            <i className="ri-information-line mt-0.5"></i>
            <span className="flex-1">{notice}</span>
          </div>
        </div>
      )}
    </div>
  );
}